import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock3,
  LogOut,
  Mail,
  User,
} from "lucide-react";
import upcomingIcon from "../../assets/global/upcoming.svg";
import completedIcon from "../../assets/global/completed.svg";
import totalSessionsIcon from "../../assets/global/calendar1.svg";
import {
  extractTeacherRecord,
  getTeacherDisplayName,
  getTeacherEmail,
  normalizeTeacherBookingPayload,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";

const dashboardStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-dashboard-page {
    min-height: 100vh;
    background: #faf5ec;
    color: #1f1a17;
    font-family: "Lato", sans-serif;
    font-size: 1rem;
  }

  .teacher-dashboard-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-dashboard-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-dashboard-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .teacher-dashboard-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-dashboard-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
  }

  .teacher-dashboard-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-dashboard-actions {
    display: flex;
    align-items: center;
    gap: clamp(0.75rem, 1.5vw, 1rem);
  }

  .teacher-dashboard-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-dashboard-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-dashboard-tabs {
    display: flex;
    align-items: center;
    gap: clamp(0.625rem, 1.5vw, 0.95rem);
    min-height: 3.35rem;
  }

  .teacher-dashboard-tab {
    min-height: 3.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding-inline: 0.95rem;
    border: 0;
    border-bottom: 0.1875rem solid transparent;
    background: transparent;
    color: #2f2a28;
    font: inherit;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .teacher-dashboard-tab-active {
    color: #2453d4;
    border-bottom-color: #2453d4;
    background: rgba(226, 235, 255, 0.78);
    border-top-left-radius: 0.875rem;
    border-top-right-radius: 0.875rem;
  }

  .teacher-dashboard-content {
    padding: clamp(1.25rem, 2.5vw, 1.65rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-dashboard-notice {
    border: 1px solid #d7e3fb;
    border-radius: 1rem;
    background: #dfe8fb;
    color: #315db7;
    padding: 0.95rem 1.1rem;
    margin-bottom: 1rem;
    font-size: 0.92rem;
  }

  .teacher-dashboard-summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 14.5rem), 1fr));
    gap: 0.875rem;
    margin-bottom: clamp(1.25rem, 2.6vw, 1.75rem);
  }

  .teacher-dashboard-summary-card {
    min-height: clamp(5.4rem, 8vw, 5.9rem);
    border-radius: 0.875rem;
    padding: clamp(0.95rem, 1.8vw, 1.2rem) clamp(0.95rem, 1.6vw, 1.25rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .teacher-dashboard-summary-card h3 {
    margin: 0 0 0.5rem;
    font-size: 0.92rem;
    font-weight: 500;
  }

  .teacher-dashboard-summary-card p {
    margin: 0;
    font-size: clamp(1.3rem, 2.2vw, 1.55rem);
    font-weight: 700;
  }

  .teacher-dashboard-summary-icon {
    width: clamp(2.55rem, 3.6vw, 2.9rem);
    height: clamp(2.55rem, 3.6vw, 2.9rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    color: #ffffff;
  }

  .teacher-dashboard-summary-icon img {
    width: 1.45rem;
    height: 1.45rem;
    display: block;
  }

  .teacher-dashboard-summary-upcoming {
    background: #f8dde6;
  }

  .teacher-dashboard-summary-upcoming p,
  .teacher-dashboard-summary-upcoming h3 {
    color: #bf6786;
  }

  .teacher-dashboard-summary-upcoming .teacher-dashboard-summary-icon {
    background: #c66c8d;
  }

  .teacher-dashboard-summary-completed {
    background: #ffefc8;
  }

  .teacher-dashboard-summary-completed p,
  .teacher-dashboard-summary-completed h3 {
    color: #b48b2f;
  }

  .teacher-dashboard-summary-completed .teacher-dashboard-summary-icon {
    background: #bb932d;
  }

  .teacher-dashboard-summary-total {
    background: #dfe8fb;
  }

  .teacher-dashboard-summary-total p,
  .teacher-dashboard-summary-total h3 {
    color: #315db7;
  }

  .teacher-dashboard-summary-total .teacher-dashboard-summary-icon {
    background: #315db7;
  }

  .teacher-dashboard-section {
    margin-top: 1.25rem;
  }

  .teacher-dashboard-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.875rem;
    margin-bottom: 1rem;
  }

  .teacher-dashboard-section-title {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  .teacher-dashboard-section-bar {
    width: 0.25rem;
    height: 1.9rem;
    border-radius: 999px;
  }

  .teacher-dashboard-section-bar-upcoming {
    background: #2453d4;
  }

  .teacher-dashboard-section-bar-completed {
    background: #1caa63;
  }

  .teacher-dashboard-section-title h2 {
    margin: 0;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-dashboard-count-pill {
    min-height: 2.3rem;
    padding-inline: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    font-size: 0.92rem;
    font-weight: 500;
  }

  .teacher-dashboard-count-pill-upcoming {
    background: #e2ebff;
    color: #2453d4;
    border: 1px solid #c6d6ff;
  }

  .teacher-dashboard-count-pill-completed {
    background: #e3f8ea;
    color: #1c8f56;
    border: 1px solid #c2efcf;
  }

  .teacher-dashboard-session-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .teacher-dashboard-session-card {
    border: 1px solid #dde5ef;
    border-radius: 0.75rem;
    background: #ffffff;
    padding: clamp(0.9rem, 1.6vw, 1.15rem);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: clamp(0.875rem, 1.5vw, 1rem);
  }

  .teacher-dashboard-session-main {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .teacher-dashboard-session-identity {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  .teacher-dashboard-avatar {
    width: clamp(2.7rem, 4.2vw, 3.15rem);
    height: clamp(2.7rem, 4.2vw, 3.15rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    color: #ffffff;
    font-size: clamp(1rem, 1.6vw, 1.25rem);
    font-weight: 700;
    flex: 0 0 auto;
  }

  .teacher-dashboard-avatar-dark {
    background: #000000;
  }

  .teacher-dashboard-avatar-muted {
    background: #9a9a9a;
  }

  .teacher-dashboard-session-name {
    margin: 0;
    font-size: clamp(0.95rem, 1.35vw, 1.02rem);
    font-weight: 600;
  }

  .teacher-dashboard-session-grade {
    margin: 0.25rem 0 0;
    color: #75706c;
    font-size: 0.9rem;
  }

  .teacher-dashboard-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .teacher-dashboard-meta-chip {
    min-height: 2.2rem;
    padding-inline: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border-radius: 0.55rem;
    background: #f5f7fb;
    color: #3d3a39;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .teacher-dashboard-subject-chip {
    width: fit-content;
    min-height: 2.3rem;
    padding-inline: 1rem;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .teacher-dashboard-subject-chip-live {
    background: #effcf2;
    color: #1b8b58;
    border: 1px solid #b9efca;
  }

  .teacher-dashboard-subject-chip-muted {
    background: #f5f5f5;
    color: #6d6a68;
    border: 1px solid #e3e3e3;
  }

  .teacher-dashboard-session-action {
    min-width: clamp(8.5rem, 15vw, 9.5rem);
    display: flex;
    justify-content: flex-end;
    flex: 0 0 auto;
  }

  .teacher-dashboard-email-button {
    width: 100%;
    min-height: 2.8rem;
    padding-inline: 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.625rem;
    border: 0;
    border-radius: 0.625rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 16px 26px rgba(255, 98, 77, 0.2);
    color: #ffffff;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
  }

  .teacher-dashboard-completed-chip {
    min-height: 2.3rem;
    padding-inline: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.625rem;
    border-radius: 999px;
    background: #e3f8ea;
    color: #2d8b57;
    border: 1px solid #c2efcf;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .teacher-dashboard-empty-copy {
    margin: 0;
    color: #75706c;
    font-size: 0.92rem;
  }

  @media (max-width: 1100px) {
    .teacher-dashboard-session-card {
      flex-direction: column;
    }

    .teacher-dashboard-session-action {
      width: 100%;
      justify-content: flex-start;
    }
  }

  @media (max-width: 768px) {
    .teacher-dashboard-topbar-row {
      min-height: auto;
      padding: 1.125rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-dashboard-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-dashboard-email-pill,
    .teacher-dashboard-logout {
      justify-content: center;
    }

    .teacher-dashboard-tabs {
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }

    .teacher-dashboard-section-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-dashboard-tab {
      white-space: nowrap;
    }
  }

  @media (max-width: 540px) {
    .teacher-dashboard-session-identity {
      align-items: flex-start;
    }

    .teacher-dashboard-meta-chip,
    .teacher-dashboard-email-pill {
      font-size: 0.9rem;
    }
  }
`;

const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const getInitials = (value = "") =>
  String(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "TD";

const parseDateOnly = (value) => {
  const dateValue = toTrimmedString(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);

  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDateLabel = (value) => {
  const parsedDate = parseDateOnly(value);

  if (!parsedDate) return toTrimmedString(value) || "Date not provided";

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeBookingRows = (bookings = [], teacherProfile = {}) =>
  bookings.map((booking, index) => {
    const bookingStudent = booking?.student || {};
    const bookingYear = booking?.year || {};
    const bookingSlot = booking?.slot || {};
    const sessionName =
      toTrimmedString(bookingStudent?.name) ||
      toTrimmedString(booking?.student_name) ||
      getTeacherDisplayName(teacherProfile);
    const sessionGrade =
      toTrimmedString(bookingYear?.label) ||
      toTrimmedString(teacherProfile?.year) ||
      "Booking";
    const sessionDate = formatDateLabel(bookingSlot?.date || bookingSlot?.start_time);
    const sessionTime =
      toTrimmedString(bookingSlot?.time_label) || "Time not provided";
    const sessionStatus = toTrimmedString(booking?.status) || "Pending";

    return {
      id: toTrimmedString(booking?.id || `booking-${index}`),
      initials: getInitials(sessionName),
      name: sessionName,
      grade: sessionGrade,
      date: sessionDate,
      time: sessionTime,
      subject: sessionStatus.charAt(0).toUpperCase() + sessionStatus.slice(1),
    };
  });

const summaryCardMeta = [
  {
    key: "upcoming",
    title: "Upcoming",
    iconSrc: upcomingIcon,
    className: "teacher-dashboard-summary-upcoming",
  },
  {
    key: "completed",
    title: "Past",
    iconSrc: completedIcon,
    className: "teacher-dashboard-summary-completed",
  },
  {
    key: "total",
    title: "Total Bookings",
    iconSrc: totalSessionsIcon,
    className: "teacher-dashboard-summary-total",
  },
];

function SessionMetaChip({ icon: Icon, children }) {
  return (
    <span className="teacher-dashboard-meta-chip">
      <Icon size={18} color="#2453d4" />
      {children}
    </span>
  );
}

function TeacherSessionCard({ session, completed = false, email }) {
  return (
    <article className="teacher-dashboard-session-card">
      <div className="teacher-dashboard-session-main">
        <div className="teacher-dashboard-session-identity">
          <div
            className={`teacher-dashboard-avatar ${
              completed
                ? "teacher-dashboard-avatar-muted"
                : "teacher-dashboard-avatar-dark"
            }`}
          >
            {session.initials}
          </div>
          <div>
            <h3 className="teacher-dashboard-session-name">{session.name}</h3>
            <p className="teacher-dashboard-session-grade">{session.grade}</p>
          </div>
        </div>

        <div className="teacher-dashboard-meta">
          <SessionMetaChip icon={CalendarDays}>{session.date}</SessionMetaChip>
          <SessionMetaChip icon={Clock3}>{session.time}</SessionMetaChip>
        </div>

        <span
          className={`teacher-dashboard-subject-chip ${
            completed
              ? "teacher-dashboard-subject-chip-muted"
              : "teacher-dashboard-subject-chip-live"
          }`}
        >
          {session.subject}
        </span>
      </div>

      <div className="teacher-dashboard-session-action">
        {completed ? (
          <span className="teacher-dashboard-completed-chip">
            <CheckCircle2 size={18} />
            Past
          </span>
        ) : email ? (
          <a className="teacher-dashboard-email-button" href={`mailto:${email}`}>
            <Mail size={18} />
            Check Email
          </a>
        ) : (
          <span className="teacher-dashboard-completed-chip">No email</span>
        )}
      </div>
    </article>
  );
}

function EmptySectionCard({ message }) {
  return (
    <article className="teacher-dashboard-session-card">
      <div className="teacher-dashboard-session-main">
        <h3 className="teacher-dashboard-session-name">Nothing to show yet</h3>
        <p className="teacher-dashboard-empty-copy">{message}</p>
      </div>
    </article>
  );
}

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);

  const profileQuery = useQuery({
    queryKey: ["teacher-dashboard-profile"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data);
    if (teacherRecord) {
      dispatch(updateTeacherProfile(teacherRecord));
    }
  }, [dispatch, profileQuery.data]);

  const teacherProfile = useMemo(
    () => extractTeacherRecord(profileQuery.data, teacherSession || {}) || teacherSession || {},
    [profileQuery.data, teacherSession]
  );

  const teacherEmail = getTeacherEmail(teacherProfile);

  const bookingsQuery = useQuery({
    queryKey: ["teacher-dashboard-bookings"],
    queryFn: teacherApi.getTeacherBookings,
    retry: false,
  });

  const bookingPayload = useMemo(
    () => normalizeTeacherBookingPayload(bookingsQuery.data),
    [bookingsQuery.data]
  );
  const upcomingSessions = useMemo(
    () => normalizeBookingRows(bookingPayload.upcoming, teacherProfile),
    [bookingPayload.upcoming, teacherProfile]
  );
  const completedSessions = useMemo(
    () => normalizeBookingRows(bookingPayload.past, teacherProfile),
    [bookingPayload.past, teacherProfile]
  );

  const summaryCards = summaryCardMeta.map((card) => {
    if (card.key === "upcoming") {
      return {
        ...card,
        value: bookingsQuery.isLoading ? "..." : bookingPayload.summary.upcoming,
      };
    }

    if (card.key === "completed") {
      return {
        ...card,
        value: bookingsQuery.isLoading ? "..." : bookingPayload.summary.past,
      };
    }

    return {
      ...card,
      value: bookingsQuery.isLoading ? "..." : bookingPayload.summary.total,
    };
  });

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const dashboardNotice = profileQuery.isError
    ? profileQuery.error?.message
    : bookingsQuery.isError
    ? bookingsQuery.error?.message
    : "";

  return (
    <>
      <style>{dashboardStyles}</style>
      <main className="teacher-dashboard-page">
        <header className="teacher-dashboard-topbar">
          <div className="teacher-dashboard-shell">
            <div className="teacher-dashboard-topbar-row">
              <div className="teacher-dashboard-brand">
                <div className="teacher-dashboard-brand-badge" aria-hidden="true">
                  <BookOpen size={20} />
                </div>
                <h1 className="teacher-dashboard-brand-title">
                  Teacher Dashboard
                </h1>
              </div>

              <div className="teacher-dashboard-actions">
                <div className="teacher-dashboard-email-pill">
                  <Mail size={18} />
                  {teacherEmail || "Teacher account"}
                </div>
                <button
                  className="teacher-dashboard-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-dashboard-shell">
          <nav className="teacher-dashboard-tabs" aria-label="Teacher tabs">
            <button
              className="teacher-dashboard-tab teacher-dashboard-tab-active"
              type="button"
              onClick={() => navigate("/teacherdashboard")}
            >
              <BookOpen size={20} />
              Bookings
            </button>
            <button
              className="teacher-dashboard-tab"
              type="button"
              onClick={() => navigate("/teacheravailability")}
            >
              <CalendarRange size={20} />
              Availability
            </button>
            <button
              className="teacher-dashboard-tab"
              type="button"
              onClick={() => navigate("/teacherprofile")}
            >
              <User size={20} />
              Profile
            </button>
          </nav>
        </div>

        <div className="teacher-dashboard-shell teacher-dashboard-content">
          {dashboardNotice ? (
            <div className="teacher-dashboard-notice">{dashboardNotice}</div>
          ) : null}

          <section className="teacher-dashboard-summary-grid">
            {summaryCards.map((card) => (
              <article
                key={card.title}
                className={`teacher-dashboard-summary-card ${card.className}`}
              >
                <div>
                  <h3>{card.title}</h3>
                  <p>{card.value}</p>
                </div>
                <div className="teacher-dashboard-summary-icon">
                  <img src={card.iconSrc} alt="" aria-hidden="true" />
                </div>
              </article>
            ))}
          </section>

          <section className="teacher-dashboard-section">
            <div className="teacher-dashboard-section-header">
              <div className="teacher-dashboard-section-title">
                <div className="teacher-dashboard-section-bar teacher-dashboard-section-bar-upcoming" />
                <h2>Upcoming Booking</h2>
              </div>
              <span className="teacher-dashboard-count-pill teacher-dashboard-count-pill-upcoming">
                {upcomingSessions.length} Slots
              </span>
            </div>

            <div className="teacher-dashboard-session-list">
              {upcomingSessions.length ? (
                upcomingSessions.map((session) => (
                  <TeacherSessionCard
                    key={`upcoming-${session.id}`}
                    session={session}
                    email={teacherEmail}
                  />
                ))
              ) : (
                <EmptySectionCard message="No upcoming availability has been returned by the backend yet." />
              )}
            </div>
          </section>

          <section className="teacher-dashboard-section">
            <div className="teacher-dashboard-section-header">
              <div className="teacher-dashboard-section-title">
                <div className="teacher-dashboard-section-bar teacher-dashboard-section-bar-completed" />
                <h2>Past Bookings</h2>
              </div>
              <span className="teacher-dashboard-count-pill teacher-dashboard-count-pill-completed">
                {completedSessions.length} Slots
              </span>
            </div>

            <div className="teacher-dashboard-session-list">
              {completedSessions.length ? (
                completedSessions.map((session) => (
                  <TeacherSessionCard
                    key={`completed-${session.id}`}
                    session={session}
                    completed
                    email={teacherEmail}
                  />
                ))
              ) : (
                <EmptySectionCard message="Past availability will appear here once the public tutor availability API returns dated slots." />
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
